import createLogger from '@sfly/js-logger';
import StoreUtil from './StoreUtil';
import StorageUtil from './StorageUtil';

const SFLY_SEARCH_LOGGER_KEY = 'Sfly-Search';
const CALLING_CLASS_KEY = 'callingClass';
const CALLING_METHOD_KEY = 'callingMethod';
const VISITOR_ID_KEY = 'visitorId';
const ERROR_ATTRIBUTE_KEY_PREFIX = 'error_';
const ERROR_MESSAGE_KEY = 'error_message';
const ERROR_STATUS_KEY = 'error_status';
const ERROR_HEADERS_KEY = 'error_headers';
const ERROR_METHOD_KEY = 'error_method';
const ERROR_URL_KEY = 'error_url';
const HTTP_RESPONSE_STATUS_KEY = 'response_status';
const HTTP_RESPONSE_HEADERS_KEY = 'response_headers';
const HTTP_RESPONSE_METHOD_KEY = 'response_method';
const HTTP_RESPONSE_URL_KEY = 'response_url';
const USER_ID_KEY = 'uid';
const EMAIL_ID_KEY = 'emailId';
const LOGGING_GIF_LOCATION_SUFFIX = '/application/log.gif';

export default class Logger {
    constructor(className, name = SFLY_SEARCH_LOGGER_KEY) {
        this.className = className;
        this.logger = createLogger(name, { resource: this.getLoggingGifURI() });
    }

    /**
     * Returns logging GIF location URI
     * @returns {string}
     */
    getLoggingGifURI() {
        return StoreUtil.getCurrentEnvAppCodeName() + LOGGING_GIF_LOCATION_SUFFIX;
    }

    /**
     * Add extra logging attributes to logging payload
     * @param additionalLoggingAttributes
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param includeUserData
     * @returns {Promise}
     */
    addExtraLoggingData(additionalLoggingAttributes, callingMethod, error, httpResponse, includeUserData = true) {
        return new Promise((resolve) => {
            additionalLoggingAttributes[CALLING_CLASS_KEY] = this.className;
            if(callingMethod) {
                additionalLoggingAttributes[CALLING_METHOD_KEY] = callingMethod;
            }
            if(error) {
                this.addErrorData(additionalLoggingAttributes, error);
            }
            if(httpResponse) {
                this.addHttpResponseData(additionalLoggingAttributes, httpResponse);
            }

            this.addCookieData(additionalLoggingAttributes);

            if(includeUserData) {
                this.addUserData(additionalLoggingAttributes)
                    .then(() => {
                        resolve();
                    })
                    .catch(() => {
                        resolve();
                    });
            }
            else {
                resolve();
            }
        });
    }

    /**
     * Add error info to logging payload
     * @param additionalLoggingAttributes
     * @param error
     */
    addErrorData(additionalLoggingAttributes, error) {
        /* Errors come in many formats.  The three I've identified from Axios are:
         1. Those containing a response object
         2. Those containing no response, but a config object
         3. Those containing a request object

         In addition, there's the "generic error" returned by many other calls. */
        if(error.response) {
            this.addErrorDataFromAxiosErrorContainingResponse(additionalLoggingAttributes, error);
        }
        else if(error.config) {
            this.addErrorDataFromAxiosErrorContainingOnlyConfig(additionalLoggingAttributes, error);
        }
        else if(error.request) {
            this.addErrorDataFromAxiorErrorContainingRequest(additionalLoggingAttributes, error);
        }
        else {
            this.addErrorDataFromGenericError(error, additionalLoggingAttributes);
        }
    }

    /**
     * Add generic error info to logging payload
     * @param error
     * @param additionalLoggingAttributes
     */
    addErrorDataFromGenericError(error, additionalLoggingAttributes) {
        if(error.message) {
            additionalLoggingAttributes[ERROR_MESSAGE_KEY] = error.message;
        }
        for (const key in error) {
            if (key !== 'message' && error.hasOwnProperty(key) && typeof error[key] !== 'object') {
                additionalLoggingAttributes[ERROR_ATTRIBUTE_KEY_PREFIX + key] = error[key];
            }
        }
    }

    /**
     * Add axios request error Info to logging payload
     * @param additionalLoggingAttributes
     * @param error
     */
    addErrorDataFromAxiorErrorContainingRequest(additionalLoggingAttributes, error) {
        //XMLHttpRequest
        additionalLoggingAttributes[ERROR_STATUS_KEY] = error.request.status;
        additionalLoggingAttributes[ERROR_URL_KEY] = error.request.responseURL;
    }

    /**
     * Add axios config info to logging payload
     * @param additionalLoggingAttributes
     * @param error
     */
    addErrorDataFromAxiosErrorContainingOnlyConfig(additionalLoggingAttributes, error) {
        additionalLoggingAttributes[ERROR_METHOD_KEY] = error.config.method;
        additionalLoggingAttributes[ERROR_URL_KEY] = error.config.url;
        additionalLoggingAttributes[ERROR_HEADERS_KEY] = JSON.stringify(error.config.headers);
    }

    /**
     * Add axios HTTP error info to logging payload
     * @param additionalLoggingAttributes
     * @param error
     */
    addErrorDataFromAxiosErrorContainingResponse(additionalLoggingAttributes, error) {
        additionalLoggingAttributes[ERROR_STATUS_KEY] = error.response.status;
        additionalLoggingAttributes[ERROR_HEADERS_KEY] = JSON.stringify(error.response.headers);
        if (error.response.config) {
            additionalLoggingAttributes[ERROR_METHOD_KEY] = error.response.config.method;
            additionalLoggingAttributes[ERROR_URL_KEY] = error.response.config.url;
        }
    }

    /**
     * Add http response info to logging payload
     * @param additionalLoggingAttributes
     * @param httpResponse
     */
    addHttpResponseData(additionalLoggingAttributes, httpResponse) {
        additionalLoggingAttributes[HTTP_RESPONSE_STATUS_KEY] = httpResponse.status;
        additionalLoggingAttributes[HTTP_RESPONSE_HEADERS_KEY] = JSON.stringify(httpResponse.headers);
        if(httpResponse.config) {
            additionalLoggingAttributes[HTTP_RESPONSE_METHOD_KEY] = httpResponse.config.method;
            additionalLoggingAttributes[HTTP_RESPONSE_URL_KEY] = httpResponse.config.url;
        }
    }

    /**
     * Add Cookie info to logging payload
     * @param additionalLoggingAttributes
     */
    addCookieData(additionalLoggingAttributes) {
        const visitorId = StorageUtil.getCookie(StoreUtil.getCurrentEnvCookieKey());
        if(visitorId) {
            additionalLoggingAttributes[VISITOR_ID_KEY] = visitorId;
        }
    }

    /**
     * Add user info to logging payload
     * @param additionalLoggingAttributes
     * @returns {Promise}
     */
    addUserData(additionalLoggingAttributes) {
        return new Promise((resolve) => {
            let currentUser;
            if(window && window.$sflyAuth && window.$sflyAuth.cog) {
                currentUser = window.$sflyAuth.cog.currentUser;
            }

            if(currentUser) {
                currentUser.getUserData()
                    .then((userData) => {
                        additionalLoggingAttributes[USER_ID_KEY] = userData.sfly_uid;
                        additionalLoggingAttributes[EMAIL_ID_KEY] = userData.emailId;
                        resolve();
                    })
                    .catch((error) => {
                        resolve();
                    });
            }
            else {
                resolve();
            }
        });
    }

    /**
     * Debug debug info by calling SFLY logger API
     * @param message
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param data
     */
    debug(message, callingMethod, error, httpResponse, data = {}) {
        this.addExtraLoggingData(data, callingMethod, error, httpResponse)
            .then(() => {
                this.logger.debug(message, data);
            })
            .catch();
    }

    /**
     * Log generic info by calling SFLY logger API
     * @param message
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param data
     */
    info(message, callingMethod, error, httpResponse, data = {}) {
        this.addExtraLoggingData(data, callingMethod, error, httpResponse)
            .then(() => {
                this.logger.info(message, data);
            })
            .catch();
    }

    /**
     * Log warning info by call SFLY logginer API
     * @param message
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param data
     */
    warn(message, callingMethod, error, httpResponse, data = {}) {
        this.addExtraLoggingData(data, callingMethod, error, httpResponse)
            .then(() => {
                this.logger.warn(message, data);
            })
            .catch();
    }

    /**
     * Log error info by calling SFLY logger API
     * @param message
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param data
     * @param includeUserData
     */
    error(message, callingMethod, error, httpResponse, data = {}, includeUserData = true) {
        this.addExtraLoggingData(data, callingMethod, error, httpResponse, includeUserData)
            .then(() => {
                this.logger.error(message, data);
            })
            .catch();
    }

    /**
     * Log metric info by calling SFLY logger API
     * @param message
     * @param callingMethod
     * @param error
     * @param httpResponse
     * @param duration
     * @param data
     */
    metric(message, callingMethod, error, httpResponse, duration, data = {}) {
        this.addExtraLoggingData(data, callingMethod, error, httpResponse)
            .then(() => {
                this.logger.metric(message, duration, data);
            })
            .catch();
    }

    metricDuration(message, duration, data = {}) {
        this.addExtraLoggingData(data, '', '', '')
            .then(() => {
                this.logger.metric(message, duration, data);
            })
            .catch();
    }
}
